<template>
  <mp-popover
    id="download-notification"
    ref="downloadPopover"
    placement="bottom-end"
    use-portal
    :close-on-blur="true"
    @open="onPopoverOpen"
    @close="onPopoverClose"
  >
    <mp-popover-trigger>
      <mp-box>
        <mp-tooltip position="bottom" label="Download" use-portal>
          <mp-button-icon
            name="download"
            size="md"
            data-qa="navbar-download-notification"
          />
        </mp-tooltip>
      </mp-box>
    </mp-popover-trigger>
    <mp-popover-content
      max-width="400px"
      min-width="360px"
      bg="white"
      rounded="md"
      shadow="lg"
      border-width="1px"
      border-color="gray.400"
      z-index="99"
    >
      <!-- Header Tabs -->
      <mp-tabs id="notification-tabs" :index="activeTabIndex" @change="onTabChange">
        <mp-tab-list mb="0" h="48px" ml="4">
          <mp-tab v-for="tab in tabs" :key="tab.key" py="0" :data-qa="`notification-tab-${tab.key}`">
            {{ tab.label }}
          </mp-tab>
        </mp-tab-list>
      </mp-tabs>

      <!-- Content -->
      <mp-box ref="scrollableContent" max-height="320px" overflow-y="auto" @scroll.native="onScroll">
        <!-- Loading state -->
        <mp-flex v-if="isLoadingCurrentTab && !isLoadingMore" justify="center" align="center" py="8">
          <mp-spinner size="md" />
        </mp-flex>

        <!-- Empty state -->
        <mp-flex
          v-else-if="!currentTabData.length"
          flex-direction="column"
          align="center"
          justify="center"
          py="8"
          px="4"
          gap="4"
        >
          <mp-icon :name="activeTab.emptyIcon" size="lg" color="gray.600" />
          <mp-text color="gray.600" font-size="sm" text-align="center">{{ activeTab.emptyText }}</mp-text>
        </mp-flex>

        <!-- Notification list -->
        <mp-box v-else>
          <mp-flex
            v-for="(item, index) in currentTabData"
            :key="item[activeTab.keyIndicator] || index"
            align="center"
            px="4"
            py="3"
            border-bottom="1px"
            border-color="gray.50"
            :bg="isLatestItem(index) ? 'ice.50' : 'white'"
            :_hover="{ bg: isLatestItem(index) ? 'ice.50' : 'gray.50' }"
            cursor="pointer"
            @click="handleItemClick(item)"
          >
            <!-- Status icon -->
            <mp-box mr="3" flex-shrink="0">
              <mp-icon
                v-if="item.status === 'COMPLETED'"
                name="done"
                size="sm"
                variant="fill"
                color="green.400"
              />
              <mp-spinner
                v-else-if="item.status === 'PENDING' || item.status === 'PROCESSING'"
                size="sm"
              />
              <mp-icon
                v-else-if="item.status === 'FAILED'"
                name="warning-circular"
                size="sm"
                color="red.400"
              />
            </mp-box>

            <!-- Content -->
            <mp-flex flex-direction="column" flex="1" min-width="0">
              <mp-text
                font-weight="semibold"
                font-size="sm"
                :color="item.status === 'FAILED' ? 'red.400' : 'dark'"
                no-of-lines="1"
              >
                {{ item.display_name }}
              </mp-text>
              <mp-text
                v-if="item.description"
                font-size="xs"
                color="gray.600"
                no-of-lines="1"
              >
                {{ item.description }}
              </mp-text>
            </mp-flex>

            <!-- Action icon based on tab type -->
            <mp-box v-if="item.status === 'COMPLETED' && activeTab.actionIcon" ml="3" flex-shrink="0">
              <mp-icon :name="activeTab.actionIcon" size="sm" color="gray.400" />
            </mp-box>
          </mp-flex>

          <!-- Loading more spinner -->
          <mp-flex v-if="isLoadingMore" justify="center" align="center" py="4">
            <mp-spinner size="sm" />
          </mp-flex>

          <!-- End of list message -->
          <mp-text v-else-if="!currentTabHasNext && currentTabData.length >= limit" text-align="center" py="3" color="gray.400" font-size="xs">
            You've reached the end of the list
          </mp-text>
        </mp-box>
      </mp-box>

      <!-- Footer -->
      <mp-flex v-if="activeTab.withFooter !== false" px="4" py="3" border-top="1px" border-color="gray.50" align="center">
        <mp-icon name="info" width="12px" height="12px" color="gray.600" mr="2" />
        <mp-text font-size="xs" color="gray.600">
          {{ activeTab.footerText }}
        </mp-text>
      </mp-flex>
    </mp-popover-content>
  </mp-popover>
</template>

<script>
import { debounce } from 'lodash-es';
import { MpSpinner, MpTooltip } from '@mekari/pixel';
import { alertMixin } from '@/mixins/general';

/**
 * Tab Configuration
 * To add a new tab, add a new entry to the NOTIFICATION_TABS array with the following properties:
 * - key: unique identifier for the tab (used for data storage and events)
 * - label: display label for the tab
 * - endpoint: API endpoint to fetch data (relative to $newEndPoint) - without pagination params
 * - emptyIcon: icon to show when no data is available
 * - emptyText: text to show when no data is available
 * - actionIcon: icon to show for completed items (optional)
 * - footerText: text to show in the footer
 * - handleClick: function to handle item click (receives item and component context)
 * - hasPendingStatus: function to check if item has pending status (for polling)
 * - keyIndicator: unique key field for list items
 */
const NOTIFICATION_TABS = [
  {
    key: 'download',
    label: 'Download',
    endpoint: '/download-manager',
    emptyIcon: 'download',
    emptyText: 'No downloads available',
    actionIcon: 'download',
    footerText: 'Download history will be kept for 5 days',
    keyIndicator: 'download_url', // since we have no id, we use download_url as unique key
    handleClick: (item, ctx) => {
      if (item.status !== 'COMPLETED') {
        if (item.status === 'FAILED') {
          ctx.alertToast('Download failed. Please try again.', 'danger');
        }
        return;
      }
      if (item.download_url) {
        window.open(item.download_url, '_blank');
      } else {
        ctx.alertToast('Download URL is not available', 'danger');
      }
    },
    hasPendingStatus: item => item.status === 'PENDING' || item.status === 'PROCESSING',
  },
  // Example: Add more tabs here in the future
  // {
  //   key: 'import',
  //   label: 'Import',
  //   endpoint: '/import-manager',
  //   emptyIcon: 'upload',
  //   emptyText: 'No imports available',
  //   actionIcon: 'newtab',
	//	 withFooter: false,
  //   footerText: 'Import history will be kept for 5 days',
  //   handleClick: (item, ctx) => { /* handle import click */ },
  //   hasPendingStatus: item => item.status === 'PENDING' || item.status === 'PROCESSING',
  // },
];

export default {
  name: 'NotificationCenter',
  components: {
    MpSpinner,
    MpTooltip,
  },
  mixins: [alertMixin],
  data() {
    return {
      activeTabIndex: 0,
      isPopoverOpen: false,
      pollingInterval: null,
      latestItemId: null, // Track the latest item to highlight
      limit: 10, // Items per page for infinite scroll
      // Dynamic state for each tab's data and loading state
      tabData: NOTIFICATION_TABS.reduce((acc, tab) => {
        acc[tab.key] = {
          data: [],
          isLoading: false,
          isLoadingMore: false,
          isFetched: false,
          currentPage: 1,
          hasNext: false,
        };
        return acc;
      }, {}),
    };
  },
  computed: {
    tabs() {
      return NOTIFICATION_TABS;
    },
    activeTab() {
      return NOTIFICATION_TABS[this.activeTabIndex] || NOTIFICATION_TABS[0];
    },
    currentTabState() {
      return this.tabData[this.activeTab.key];
    },
    currentTabData() {
      return this.currentTabState?.data || [];
    },
    isLoadingCurrentTab() {
      return this.currentTabState?.isLoading || false;
    },
    isLoadingMore() {
      return this.currentTabState?.isLoadingMore || false;
    },
    currentTabHasNext() {
      return this.currentTabState?.hasNext || false;
    },
    hasPendingItems() {
      const tab = this.activeTab;
      if (!tab.hasPendingStatus) return false;
      return this.currentTabData.some(item => tab.hasPendingStatus(item));
    },
  },
  mounted() {
    this.$root.$on('open-download-notification', this.openPopoverAndHighlight);
  },
  beforeDestroy() {
    this.stopPolling();
    this.$root.$off('open-download-notification', this.openPopoverAndHighlight);
  },
  methods: {
    // Check if item is the latest (first item after fresh fetch triggered by event)
    isLatestItem(index) {
      return index === 0 && this.latestItemId !== null && this.currentTabData[0]?.[this.activeTab.keyIndicator] === this.latestItemId;
    },
    openPopover() {
      if (this.$refs.downloadPopover) {
        this.$refs.downloadPopover.openPopover();
      }
    },
    async openPopoverAndHighlight() {
      // Switch to download tab when triggered by download event
      this.activeTabIndex = this.tabs.findIndex(t => t.key === 'download');
      
      // Reset fetched state to force refetch and get latest item
      if (this.tabData.download) {
        this.tabData.download.isFetched = false;
        this.tabData.download.currentPage = 1;
        this.tabData.download.data = [];
      }
      
      this.openPopover();
      
      // After fetch, mark the first item as latest
      await this.$nextTick();
      if (this.currentTabData.length > 0) {
        this.latestItemId = this.currentTabData[0]?.[this.activeTab.keyIndicator] || null;
      }
    },
    async onPopoverOpen() {
      this.isPopoverOpen = true;
      await this.fetchCurrentTabDataIfNeeded();
      this.startPollingIfNeeded();
    },
    onPopoverClose() {
      this.isPopoverOpen = false;
      this.latestItemId = null; // Clear highlight when popover closes
      this.stopPolling();
    },
    async onTabChange(index) {
      this.activeTabIndex = index;
      this.latestItemId = null; // Clear highlight when switching tabs
      this.stopPolling();
      await this.fetchCurrentTabDataIfNeeded();
      this.startPollingIfNeeded();
    },
    async fetchCurrentTabDataIfNeeded(force = false) {
      const tabKey = this.activeTab.key;
      const tabState = this.tabData[tabKey];
      
      // Skip if already fetched and not forcing refetch
      if (tabState.isFetched && tabState.data.length > 0 && !force) {
        return;
      }
      
      await this.fetchTabData(tabKey);
    },
    async fetchTabData(tabKey, isLoadMore = false) {
      const tab = NOTIFICATION_TABS.find(t => t.key === tabKey);
      if (!tab) return;
      
      const tabState = this.tabData[tabKey];
      
      // Set appropriate loading state
      if (isLoadMore) {
        tabState.isLoadingMore = true;
      } else {
        tabState.isLoading = true;
      }
      
      try {
        const page = tabState.currentPage;
        const url = `${this.$newEndPoint}${tab.endpoint}?page=${page}&limit=${this.limit}`;
        const response = await this.$axios.get(url);
        
        if (response.data?.status_code === 200) {
          const contents = response.data?.contents;
          const newData = contents?.data || [];
          
          // Append data if loading more, otherwise replace
          if (isLoadMore) {
            tabState.data = [...tabState.data, ...newData];
          } else {
            tabState.data = newData;
          }
          
          // Update pagination state
          tabState.hasNext = !!contents?.next_page_url;
        } else {
          if (!isLoadMore) {
            tabState.data = [];
          }
          tabState.hasNext = false;
        }
        tabState.isFetched = true;
        
        // Update latest item ID after fetch if this was triggered by event (only on first page)
        if (!isLoadMore && tabState.data.length > 0 && this.latestItemId === null && tabKey === 'download') {
          this.latestItemId = tabState.data[0]?.[this.activeTab.keyIndicator] || null;
        }
        
        // Start polling if there are pending items
        this.startPollingIfNeeded();
      } catch (error) {
        console.error(`Error fetching ${tabKey} data:`, error);
        if (!isLoadMore) {
          tabState.data = [];
        }
        tabState.hasNext = false;
      } finally {
        tabState.isLoading = false;
        tabState.isLoadingMore = false;
      }
    },
    startPollingIfNeeded() {
      if (this.hasPendingItems && this.isPopoverOpen && !this.pollingInterval) {
        this.pollingInterval = setInterval(async () => {
          if (!this.isPopoverOpen) {
            this.stopPolling();
            return;
          }
          
          // Force refetch current tab
          await this.fetchTabData(this.activeTab.key);
          
          // Stop polling if no more pending items
          if (!this.hasPendingItems) {
            this.stopPolling();
          }
        }, 7000);
      }
    },
    stopPolling() {
      if (this.pollingInterval) {
        clearInterval(this.pollingInterval);
        this.pollingInterval = null;
      }
    },
    handleItemClick(item) {
      const tab = this.activeTab;
      if (item[this.activeTab.keyIndicator] === this.latestItemId) {
        // Clear highlight after click on latest item
        this.latestItemId = null;
      }
      if (tab.handleClick) {
        tab.handleClick(item, this);
      }
    },
    // Infinite scroll handler with debounce
    onScroll: debounce(function(event) {
      const { scrollTop, clientHeight, scrollHeight } = event.target;
      const pixelTolerance = 30;
      const tabState = this.currentTabState;
      
      // Check if we're near the bottom and should load more
      if (
        !tabState.isLoading &&
        !tabState.isLoadingMore &&
        tabState.hasNext &&
        scrollTop + clientHeight + pixelTolerance >= scrollHeight
      ) {
        tabState.currentPage += 1;
        this.fetchTabData(this.activeTab.key, true);
      }
    }, 200),
    // Public method to refresh specific tab data (can be called from parent)
    async refreshTabData(tabKey = 'download') {
      const tabState = this.tabData[tabKey];
      if (tabState) {
        tabState.currentPage = 1;
        tabState.data = [];
        tabState.isFetched = false;
      }
      await this.fetchTabData(tabKey);
    },
  },
};
</script>
